import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { useLang } from "@/i18n/LanguageContext";
import Logo from "@/components/Logo";
import ProductEditor from "@/components/admin/ProductEditor";
import BlogEditor from "@/components/admin/BlogEditor";
import { ChevronDown, ChevronRight, Edit2, Eye, Plus, Trash2 } from "lucide-react";

const STATUSES = ["pending_payment", "pending_transfer", "paid", "shipped", "cancelled", "refunded"];

function StatusPill({ status, t }) {
  const label = t(`admin.status_map.${status}`);
  const color =
    status === "paid"
      ? "bg-olive-700 text-cream-100"
      : status === "shipped"
      ? "bg-gold text-olive-900"
      : status === "cancelled" || status === "refunded"
      ? "bg-charcoal text-cream-100"
      : "bg-cream-300 text-olive-800";
  return (
    <span className={`inline-flex px-2.5 py-1 text-[10px] uppercase tracking-[0.24em] ${color}`}>
      {label}
    </span>
  );
}

export default function AdminDashboardPage() {
  const { token, checking, admin, logout, authFetch } = useAdminAuth();
  const { t, lang, toggleLang } = useLang();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving] = useState({});
  const [editing, setEditing] = useState(null); // null | "new" | product object
  const [editingPost, setEditingPost] = useState(null); // null | "new" | post object
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletePostConfirm, setDeletePostConfirm] = useState(null);

  const load = useCallback(async () => {
    const [o, p, c, b] = await Promise.all([
      authFetch("/admin/orders"),
      authFetch("/admin/products"),
      authFetch("/admin/customers"),
      authFetch("/admin/blog/posts"),
    ]);
    setOrders(o.data);
    setProducts(p.data);
    setCustomers(c.data);
    setBlogPosts(b.data);
  }, [authFetch]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  if (checking) return <div className="min-h-screen bg-cream-100 grid place-items-center text-olive-800/60">…</div>;
  if (!token) return <Navigate to="/admin/login" replace />;

  const updateStatus = async (id, status) => {
    await authFetch(`/admin/orders/${id}/status`, { method: "PATCH", data: { status } });
    load();
  };

  const updateStock = async (id, stock) => {
    setSaving((s) => ({ ...s, [id]: true }));
    try {
      await authFetch(`/admin/products/${id}/stock`, { method: "PATCH", data: { stock: parseInt(stock, 10) } });
    } finally {
      setSaving((s) => ({ ...s, [id]: false }));
      load();
    }
  };

  const deleteProduct = async (product) => {
    await authFetch(`/admin/products/${product.id}`, { method: "DELETE" });
    setDeleteConfirm(null);
    load();
  };

  const deletePost = async (post) => {
    await authFetch(`/admin/blog/posts/${post.id}`, { method: "DELETE" });
    setDeletePostConfirm(null);
    load();
  };

  const euro = (n) => (n || 0).toFixed(2).replace(".", ",") + " €";
  const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(lang === "es" ? "es-ES" : "en-GB") : "—");

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-cream-100 text-charcoal">
      <header className="border-b border-cream-400 bg-cream-100 sticky top-0 z-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between gap-4">
          <Logo />
          <div className="flex items-center gap-3">
            <button
              onClick={toggleLang}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-[0.24em] text-olive-800 border border-olive-700/30"
            >
              <span className={lang === "es" ? "text-olive-900 font-medium" : "text-olive-800/50"}>ES</span>
              <span className="text-olive-800/40">/</span>
              <span className={lang === "en" ? "text-olive-900 font-medium" : "text-olive-800/50"}>EN</span>
            </button>
            <span className="hidden sm:inline text-xs text-olive-800/60">{admin?.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="px-4 py-2 border border-olive-700/40 text-[11px] uppercase tracking-[0.24em] text-olive-800 hover:bg-cream-200"
            >
              {t("admin.logout")}
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
        <h1 className="font-serif text-3xl sm:text-4xl text-olive-900">{t("admin.dashboard_title")}</h1>

        <div className="mt-8 inline-flex flex-wrap border border-olive-700/30">
          <button
            onClick={() => setTab("orders")}
            data-testid="admin-tab-orders"
            className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.24em] ${
              tab === "orders" ? "bg-olive-700 text-cream-100" : "text-olive-800 hover:bg-cream-200"
            }`}
          >
            {t("admin.orders_tab")} ({orders.length})
          </button>
          <button
            onClick={() => setTab("customers")}
            data-testid="admin-tab-customers"
            className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.24em] border-l border-olive-700/30 ${
              tab === "customers" ? "bg-olive-700 text-cream-100" : "text-olive-800 hover:bg-cream-200"
            }`}
          >
            {t("admin.customers_tab")} ({customers.length})
          </button>
          <button
            onClick={() => setTab("products")}
            data-testid="admin-tab-products"
            className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.24em] border-l border-olive-700/30 ${
              tab === "products" ? "bg-olive-700 text-cream-100" : "text-olive-800 hover:bg-cream-200"
            }`}
          >
            {t("admin.products_tab")} ({products.length})
          </button>
          <button
            onClick={() => setTab("blog")}
            data-testid="admin-tab-blog"
            className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.24em] border-l border-olive-700/30 ${
              tab === "blog" ? "bg-olive-700 text-cream-100" : "text-olive-800 hover:bg-cream-200"
            }`}
          >
            {t("admin.blog_tab")} ({blogPosts.length})
          </button>
        </div>

        {tab === "orders" && (
          <div className="mt-8 overflow-x-auto" data-testid="admin-orders-list">
            {orders.length === 0 ? (
              <div className="text-olive-800/60 font-light">{t("admin.no_orders")}</div>
            ) : (
              <table className="w-full min-w-[720px] text-sm border border-cream-400">
                <thead className="bg-cream-200 text-[10px] uppercase tracking-[0.24em] text-olive-800/70">
                  <tr>
                    <th className="text-left p-3">{t("admin.order_number")}</th>
                    <th className="text-left p-3">{t("admin.date")}</th>
                    <th className="text-left p-3">{t("admin.customer")}</th>
                    <th className="text-right p-3">{t("admin.total")}</th>
                    <th className="text-left p-3">{t("admin.status")}</th>
                    <th className="text-left p-3">{t("admin.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-t border-cream-400 align-top">
                      <td className="p-3 font-serif text-olive-900">{o.order_number}</td>
                      <td className="p-3 text-olive-800/70 font-light text-xs">
                        {new Date(o.created_at).toLocaleString()}
                      </td>
                      <td className="p-3">
                        <div className="font-medium text-olive-900">{o.customer?.name}</div>
                        <div className="text-xs text-olive-800/70 font-light">{o.customer?.email}</div>
                        <div className="text-xs text-olive-800/70 font-light">
                          {o.customer?.city}, {o.customer?.postal_code}
                        </div>
                      </td>
                      <td className="p-3 text-right font-serif">{euro(o.total)}</td>
                      <td className="p-3"><StatusPill status={o.status} t={t} /></td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <select
                            value={o.status}
                            onChange={(e) => updateStatus(o.id, e.target.value)}
                            data-testid={`admin-status-${o.order_number}`}
                            className="bg-cream-100 border border-cream-400 px-2 py-1 text-xs text-olive-900"
                          >
                            {STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {t(`admin.status_map.${s}`)}
                              </option>
                            ))}
                          </select>
                          <Link
                            to={`/admin/pedido/${o.id}`}
                            data-testid={`admin-view-${o.order_number}`}
                            className="inline-flex items-center gap-1 px-3 py-1 border border-olive-700/40 text-[10px] uppercase tracking-[0.24em] text-olive-800 hover:bg-cream-200"
                          >
                            <Eye size={12} /> Ver
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "customers" && (
          <div className="mt-8" data-testid="admin-customers-list">
            <p className="text-sm text-olive-800/70 font-light mb-6">{t("admin.customers_subtitle")}</p>
            {customers.length === 0 ? (
              <div className="text-olive-800/60 font-light">{t("admin.no_customers")}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[860px] text-sm border border-cream-400">
                  <thead className="bg-cream-200 text-[10px] uppercase tracking-[0.24em] text-olive-800/70">
                    <tr>
                      <th className="text-left p-3 w-8"></th>
                      <th className="text-left p-3">{t("admin.customer_name")}</th>
                      <th className="text-left p-3">{t("admin.customer_email")}</th>
                      <th className="text-left p-3">{t("admin.customer_phone")}</th>
                      <th className="text-left p-3">{t("admin.customer_location")}</th>
                      <th className="text-right p-3">{t("admin.customer_orders")}</th>
                      <th className="text-right p-3">{t("admin.customer_paid")}</th>
                      <th className="text-right p-3">{t("admin.customer_spent")}</th>
                      <th className="text-left p-3">{t("admin.customer_last_order")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((c) => {
                      const open = !!expanded[c.email];
                      return (
                        <React.Fragment key={c.email}>
                          <tr
                            className="border-t border-cream-400 align-top hover:bg-cream-200/60 cursor-pointer"
                            onClick={() => setExpanded({ ...expanded, [c.email]: !open })}
                            data-testid={`admin-customer-row-${c.email}`}
                          >
                            <td className="p-3 text-olive-800/60">
                              {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </td>
                            <td className="p-3 font-medium text-olive-900">
                              {c.name}
                              <span className="ml-2 text-[10px] uppercase tracking-[0.24em] text-olive-800/50">
                                {c.lang}
                              </span>
                            </td>
                            <td className="p-3 text-olive-800/85 break-all">
                              <a
                                href={`mailto:${c.email}`}
                                onClick={(e) => e.stopPropagation()}
                                className="hover:text-gold-deep"
                              >
                                {c.email}
                              </a>
                            </td>
                            <td className="p-3 text-olive-800/85">
                              <a
                                href={`tel:${c.phone}`}
                                onClick={(e) => e.stopPropagation()}
                                className="hover:text-gold-deep"
                              >
                                {c.phone}
                              </a>
                            </td>
                            <td className="p-3 text-olive-800/85">
                              {c.city}
                              {c.province && c.province !== c.city ? `, ${c.province}` : ""}
                              <div className="text-xs text-olive-800/60">{c.postal_code} · {c.country}</div>
                            </td>
                            <td className="p-3 text-right font-serif text-olive-900">{c.total_orders}</td>
                            <td className="p-3 text-right font-serif text-olive-900">{c.paid_orders}</td>
                            <td className="p-3 text-right font-serif text-olive-900">{euro(c.total_spent)}</td>
                            <td className="p-3 text-xs text-olive-800/80">{fmtDate(c.last_order_at)}</td>
                          </tr>
                          {open && (
                            <tr className="bg-cream-200/60">
                              <td></td>
                              <td colSpan={8} className="p-5">
                                <div className="grid md:grid-cols-2 gap-6">
                                  <div>
                                    <div className="text-[10px] uppercase tracking-[0.28em] text-olive-800/60 mb-2">
                                      {t("admin.customer_address")}
                                    </div>
                                    <div className="font-light text-olive-900/90 text-sm leading-relaxed">
                                      {c.address1}
                                      {c.address2 && <><br />{c.address2}</>}
                                      <br />
                                      {c.postal_code} {c.city}
                                      {c.province && c.province !== c.city ? ` (${c.province})` : ""}
                                      <br />
                                      {c.country}
                                    </div>
                                    <div className="mt-4 text-xs text-olive-800/60 font-light">
                                      <div>
                                        {t("admin.customer_first_order")}: {fmtDate(c.first_order_at)}
                                      </div>
                                      <div>
                                        {t("admin.customer_last_order")}: {fmtDate(c.last_order_at)}
                                      </div>
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-[10px] uppercase tracking-[0.28em] text-olive-800/60 mb-2">
                                      {t("admin.orders_tab")} ({c.orders.length})
                                    </div>
                                    <ul className="space-y-2">
                                      {c.orders.map((o) => (
                                        <li
                                          key={o.order_number}
                                          className="flex items-center justify-between gap-3 p-2 bg-cream-100 border border-cream-400 text-sm"
                                        >
                                          <div>
                                            <div className="font-serif text-olive-900">{o.order_number}</div>
                                            <div className="text-xs text-olive-800/60 font-light">
                                              {fmtDate(o.created_at)} · {o.payment_method}
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-3">
                                            <span className="font-serif text-olive-900">{euro(o.total)}</span>
                                            <StatusPill status={o.status} t={t} />
                                          </div>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "products" && (
          <div className="mt-8" data-testid="admin-products-list">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <p className="text-sm text-olive-800/70 font-light">
                {products.length} producto{products.length === 1 ? "" : "s"} en catálogo. Crea, edita o retira productos. Los productos no publicados no aparecen en la tienda ni en el feed.
              </p>
              <button
                type="button"
                onClick={() => setEditing("new")}
                data-testid="admin-product-new"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-olive-700 text-cream-100 text-[11px] uppercase tracking-[0.24em] hover:bg-olive-900"
              >
                <Plus size={14} /> Nuevo producto
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {products.map((p) => (
                <div key={p.id} className="p-5 border border-cream-400 bg-cream-100">
                  <div className="flex gap-4">
                    <div className="w-20 h-24 bg-cream-200 overflow-hidden shrink-0">
                      {p.image_url ? (
                        <img src={p.image_url} alt={p.name_es} className="h-full w-full object-contain p-1.5" />
                      ) : (
                        <div className="h-full w-full grid place-items-center text-[10px] text-olive-800/40 uppercase tracking-[0.2em]">Sin imagen</div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-serif text-olive-900 text-lg truncate">
                          {lang === "es" ? p.name_es : p.name_en}
                        </div>
                        {p.active ? (
                          <span className="text-[9px] uppercase tracking-[0.24em] bg-olive-700 text-cream-100 px-2 py-0.5">
                            Publicado
                          </span>
                        ) : (
                          <span className="text-[9px] uppercase tracking-[0.24em] bg-cream-300 text-olive-800/70 px-2 py-0.5">
                            Borrador
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-olive-800/60">/{p.slug}</div>
                      <div className="mt-2 text-sm font-serif text-olive-900">{euro(p.price)}</div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-3 flex-wrap">
                    <label className="text-[10px] uppercase tracking-[0.24em] text-olive-800/60">
                      {t("admin.stock")}
                    </label>
                    <input
                      type="number"
                      min={0}
                      defaultValue={p.stock}
                      onBlur={(e) => updateStock(p.id, e.target.value)}
                      data-testid={`admin-stock-${p.slug}`}
                      className="w-24 bg-cream-200 border border-cream-400 px-3 py-1.5 text-olive-900 text-sm"
                    />
                    {saving[p.id] && <span className="text-xs text-olive-800/60">…</span>}
                    <div className="ml-auto flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setEditing(p)}
                        data-testid={`admin-edit-${p.slug}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-olive-700/40 text-[10px] uppercase tracking-[0.24em] text-olive-800 hover:bg-cream-200"
                      >
                        <Edit2 size={12} /> Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteConfirm(p)}
                        data-testid={`admin-delete-${p.slug}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-700/40 text-[10px] uppercase tracking-[0.24em] text-red-800 hover:bg-red-50"
                      >
                        <Trash2 size={12} /> Borrar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "blog" && (
          <div className="mt-8" data-testid="admin-blog-list">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <p className="text-sm text-olive-800/70 font-light">
                {blogPosts.length} publicación{blogPosts.length === 1 ? "" : "es"} en el diario. Los borradores no aparecen en la web ni en el sitemap.
              </p>
              <button
                type="button"
                onClick={() => setEditingPost("new")}
                data-testid="admin-blog-new"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-olive-700 text-cream-100 text-[11px] uppercase tracking-[0.24em] hover:bg-olive-900"
              >
                <Plus size={14} /> Nueva publicación
              </button>
            </div>
            {blogPosts.length === 0 ? (
              <div className="text-olive-800/60 font-light">Aún no hay publicaciones.</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {blogPosts.map((p) => (
                  <div key={p.id} className="p-5 border border-cream-400 bg-cream-100">
                    <div className="flex gap-4">
                      <div className="w-24 h-20 bg-cream-200 overflow-hidden shrink-0 border border-cream-400">
                        {p.hero_image ? (
                          <img src={p.hero_image} alt={p.title_es} className="h-full w-full object-cover" />
                        ) : (
                          <div className="h-full w-full grid place-items-center text-[10px] text-olive-800/40 uppercase tracking-[0.2em]">
                            Sin imagen
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-serif text-olive-900 text-base leading-snug">
                            {lang === "es" ? p.title_es : p.title_en}
                          </div>
                          {p.published ? (
                            <span className="text-[9px] uppercase tracking-[0.24em] bg-olive-700 text-cream-100 px-2 py-0.5 shrink-0">
                              Publicado
                            </span>
                          ) : (
                            <span className="text-[9px] uppercase tracking-[0.24em] bg-cream-300 text-olive-800/70 px-2 py-0.5 shrink-0">
                              Borrador
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-olive-800/60 font-light">
                          /blog/{p.slug_es} · /journal/{p.slug_en}
                        </div>
                        <div className="mt-1 text-xs text-olive-800/60 font-light">
                          {fmtDate(p.date)} · {p.read_time || "—"}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingPost(p)}
                        data-testid={`admin-blog-edit-${p.slug_es}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-olive-700/40 text-[10px] uppercase tracking-[0.24em] text-olive-800 hover:bg-cream-200"
                      >
                        <Edit2 size={12} /> Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeletePostConfirm(p)}
                        data-testid={`admin-blog-delete-${p.slug_es}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-700/40 text-[10px] uppercase tracking-[0.24em] text-red-800 hover:bg-red-50"
                      >
                        <Trash2 size={12} /> Borrar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {editing !== null && (
          <ProductEditor
            product={editing === "new" ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={load}
          />
        )}

        {editingPost !== null && (
          <BlogEditor
            post={editingPost === "new" ? null : editingPost}
            onClose={() => setEditingPost(null)}
            onSaved={load}
          />
        )}

        {deletePostConfirm && (
          <div className="fixed inset-0 z-[9999] bg-charcoal/60 grid place-items-center p-4" data-testid="delete-post-confirm">
            <div className="bg-cream-100 border border-cream-400 max-w-md w-full p-6">
              <h3 className="font-serif text-xl text-olive-900">¿Borrar publicación?</h3>
              <p className="mt-3 text-sm text-olive-800/80">
                Vas a eliminar <strong>{deletePostConfirm.title_es}</strong> (<code className="text-xs">/blog/{deletePostConfirm.slug_es}</code>). Los enlaces existentes en buscadores dejarán de resolver.
              </p>
              <div className="mt-5 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setDeletePostConfirm(null)}
                  className="px-4 py-2 border border-olive-700/40 text-[11px] uppercase tracking-[0.24em] text-olive-800 hover:bg-cream-200"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => deletePost(deletePostConfirm)}
                  data-testid="delete-post-confirm-yes"
                  className="px-5 py-2.5 bg-red-800 text-cream-100 text-[11px] uppercase tracking-[0.24em] hover:bg-red-900"
                >
                  Sí, borrar
                </button>
              </div>
            </div>
          </div>
        )}

        {deleteConfirm && (
          <div className="fixed inset-0 z-[9999] bg-charcoal/60 grid place-items-center p-4" data-testid="delete-confirm">
            <div className="bg-cream-100 border border-cream-400 max-w-md w-full p-6">
              <h3 className="font-serif text-xl text-olive-900">¿Borrar producto?</h3>
              <p className="mt-3 text-sm text-olive-800/80">
                Vas a eliminar <strong>{deleteConfirm.name_es}</strong> (<code className="text-xs">/{deleteConfirm.slug}</code>) del catálogo.
                Los pedidos antiguos que lo contengan no se modifican, pero el producto dejará de aparecer en tienda y feed.
              </p>
              <div className="mt-5 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 border border-olive-700/40 text-[11px] uppercase tracking-[0.24em] text-olive-800 hover:bg-cream-200"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => deleteProduct(deleteConfirm)}
                  data-testid="delete-confirm-yes"
                  className="px-5 py-2.5 bg-red-800 text-cream-100 text-[11px] uppercase tracking-[0.24em] hover:bg-red-900"
                >
                  Sí, borrar
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
